/**
 * Protected Route Component (AuthGuard)
 *
 * Guard for protecting routes that require authentication.
 * Redirects unauthenticated users to login page.
 * Allows authenticated users to access both /change-password and other protected routes.
 * Used in combination with PasswordChangeGuard for password enforcement.
 */

import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Typography } from '@mui/material';
import { useAuthState } from '../hooks/useAuthHooks';

/**
 * ProtectedRoute (AuthGuard) - Requires authentication to access
 *
 * Behavior:
 * - While the session is being restored after a page load: loader, no redirect and no Outlet,
 *   so no protected page mounts and sends requests with an expired token
 * - If not authenticated: redirect to /login
 * - If authenticated: render child routes (Outlet)
 *   - This includes /change-password which must be accessible
 *   - PasswordChangeGuard handles further restrictions for other routes
 *
 * Usage:
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/change-password" element={<ChangePasswordPage />} />
 *   <Route element={<PasswordChangeGuard />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 * </Route>
 */
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, sessionRestoring } = useAuthState();

  if (sessionRestoring) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Typography>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
